import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { UseOutletContextAccounting } from '../Accounting';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { FiscalYearDA } from '../../../../services/admin/fiscal.year.service';
import { Error } from '../../../../models/error.model';
import { GlobalSwal } from '../../../../styles/swal.style';
import { getError } from '../../../../languages/translations/response';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';
import { dateFormat } from '../../../../scripts/date.time.format.script';
import { modalShow, modalHide } from '../../../../utils/modal.util';

import AppPreloader from '../../../../components/Preloader';

export interface FiscalYearListPageProps {};

const FiscalYearListPage: React.FunctionComponent<FiscalYearListPageProps> = props => {
  const {setRoute, companyForUser} = UseOutletContextAccounting()
  const {lang} = StateLanguage()

  const [fiscalYears, setFiscalYears] = useState<any[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [closing, setClosing] = useState(false)
  const [reopening, setReopening] = useState(false)
  const [deleting, setDeleting] = useState(false)

  // Create form state
  const [formName, setFormName] = useState('')
  const [formStartDate, setFormStartDate] = useState('')
  const [formEndDate, setFormEndDate] = useState('')
  const [creating, setCreating] = useState(false)

  // Edit form state
  const [editingFy, setEditingFy] = useState<any>(null)
  const [editName, setEditName] = useState('')
  const [editStartDate, setEditStartDate] = useState('')
  const [editEndDate, setEditEndDate] = useState('')
  const [updating, setUpdating] = useState(false)
  const [editError, setEditError] = useState('')

  useEffect(() => {
    setRoute && setRoute({path: {root: lang.labels.accounting, branch: lang.labels.fiscalYears}, company: false})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (companyForUser) {
      loadFiscalYears()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser])

  const loadFiscalYears = async () => {
    const id = companyForUser?.id ?? (companyForUser as any)?._id
    if (!id) { setFiscalYears([]); return }

    try {
      const response = await FiscalYearDA.getFiscalYears(id)
      if (response.status === 200) {
        const data = response.data
        const items = Array.isArray(data) ? data : data?.items ?? data?.fiscal_years ?? []
        setFiscalYears(items)
      } else {
        setFiscalYears([])
        const error = response.data as Error
        errorWebHandler(error)
        GlobalSwal.fire({ title: getError(error.code, lang.code, error.message), icon: 'error' })
      }
    } catch {
      setFiscalYears([])
    }
  }

  const handleCreate = async () => {
    if (!formName.trim() || !formStartDate || !formEndDate) return
    const id = companyForUser?.id ?? (companyForUser as any)?._id
    if (!id) return

    setCreating(true)
    try {
      const response = await FiscalYearDA.createFiscalYear(id, {
        name: formName.trim(),
        start_date: formStartDate,
        end_date: formEndDate
      })
      if (response.status === 200 || response.status === 201) {
        modalHide('modal-create-fiscal-year')
        setFormName('')
        setFormStartDate('')
        setFormEndDate('')
        await loadFiscalYears()
      } else {
        const error = response.data as Error
        errorWebHandler(error)
        GlobalSwal.fire({ title: getError(error.code, lang.code, error.message), icon: 'error' })
      }
    } catch {
      GlobalSwal.fire({ title: lang.labels.error, icon: 'error' })
    } finally {
      setCreating(false)
    }
  }

  const handleClose = async (fy: any) => {
    const id = companyForUser?.id ?? (companyForUser as any)?._id
    const fyId = fy.id ?? fy._id
    if (!id || !fyId) return

    const result = await GlobalSwal.fire({
      title: lang.labels.closeFiscalYear,
      html: `<p class="fw-bold text-danger">${lang.labels.closeFiscalYearWarning}</p><p>${lang.labels.closeFiscalYearConfirm}</p>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: lang.labels.closeFiscalYear,
      cancelButtonText: lang.labels.cancel,
      confirmButtonColor: '#dc3545',
    })

    if (!result.isConfirmed) return

    setClosing(true)
    try {
      const response = await FiscalYearDA.closeFiscalYear(id, fyId)
      if (response.status === 200) {
        await loadFiscalYears()
      } else {
        const error = response.data as Error
        errorWebHandler(error)
        GlobalSwal.fire({ title: getError(error.code, lang.code, error.message), icon: 'error' })
      }
    } catch {
      GlobalSwal.fire({ title: lang.labels.error, icon: 'error' })
    } finally {
      setClosing(false)
    }
  }

  const canReopenFiscalYear = (fy: any): { allowed: boolean, blockerName?: string } => {
    if (!fiscalYears || fy.status !== 'closed') return { allowed: false }
    const fyEnd = fy.end_date ?? ''
    const laterClosed = fiscalYears.find((other: any) => other.status === 'closed' && (other.end_date ?? '') > fyEnd)
    if (laterClosed) return { allowed: false, blockerName: laterClosed.name }
    return { allowed: true }
  }

  const handleReopen = async (fy: any) => {
    const id = companyForUser?.id ?? (companyForUser as any)?._id
    const fyId = fy.id ?? fy._id
    if (!id || !fyId) return

    const result = await GlobalSwal.fire({
      title: lang.labels.reopenFiscalYear,
      html: `<p class="fw-bold text-warning">${lang.labels.reopenFiscalYearWarning}</p><p>${lang.labels.reopenFiscalYearConfirm}</p>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: lang.labels.reopenFiscalYear,
      cancelButtonText: lang.labels.cancel,
      confirmButtonColor: '#ffc107',
    })

    if (!result.isConfirmed) return

    setReopening(true)
    try {
      const response = await FiscalYearDA.reopenFiscalYear(id, fyId)
      if (response.status === 200) {
        await loadFiscalYears()
      } else {
        const error = response.data as Error
        errorWebHandler(error)
        GlobalSwal.fire({ title: getError(error.code, lang.code, error.message), icon: 'error' })
        await loadFiscalYears()
      }
    } catch {
      GlobalSwal.fire({ title: lang.labels.error, icon: 'error' })
    } finally {
      setReopening(false)
    }
  }

  const openEditModal = (fy: any) => {
    setEditingFy(fy)
    setEditName(fy.name ?? '')
    setEditStartDate(fy.start_date ? fy.start_date.substring(0, 10) : '')
    setEditEndDate(fy.end_date ? fy.end_date.substring(0, 10) : '')
    setEditError('')
    modalShow('modal-edit-fiscal-year')
  }

  const handleUpdate = async () => {
    if (!editName.trim() || !editStartDate || !editEndDate) return
    if (editEndDate <= editStartDate) return
    const id = companyForUser?.id ?? (companyForUser as any)?._id
    const fyId = editingFy?.id ?? editingFy?._id
    if (!id || !fyId) return

    setUpdating(true)
    setEditError('')
    try {
      const response = await FiscalYearDA.updateFiscalYear(id, fyId, {
        name: editName.trim(),
        start_date: editStartDate,
        end_date: editEndDate
      })
      if (response.status === 200) {
        modalHide('modal-edit-fiscal-year')
        setEditingFy(null)
        await loadFiscalYears()
      } else {
        const error = response.data as any
        setEditError(error?.message ?? getError(error?.code, lang.code, error?.message))
      }
    } catch {
      setEditError(lang.labels.error)
    } finally {
      setUpdating(false)
    }
  }

  const handleDelete = async (fy: any) => {
    const id = companyForUser?.id ?? (companyForUser as any)?._id
    const fyId = fy.id ?? fy._id
    if (!id || !fyId) return

    const result = await GlobalSwal.fire({
      title: lang.labels.deleteFiscalYear,
      text: lang.labels.confirmDeleteFiscalYear,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: lang.labels.delete,
      cancelButtonText: lang.labels.cancel,
      confirmButtonColor: '#dc3545',
    })

    if (!result.isConfirmed) return

    setDeleting(true)
    try {
      const response = await FiscalYearDA.deleteFiscalYear(id, fyId)
      if (response.status === 200 || response.status === 204) {
        await loadFiscalYears()
      } else {
        const error = response.data as any
        GlobalSwal.fire({ title: error?.message ?? getError(error?.code, lang.code, error?.message), icon: 'error' })
      }
    } catch {
      GlobalSwal.fire({ title: lang.labels.error, icon: 'error' })
    } finally {
      setDeleting(false)
    }
  }

  const statusBadge = (status: string) => {
    switch (status) {
      case 'closed': return <span className="badge badge-light-danger">{lang.labels.closed}</span>
      case 'open': return <span className="badge badge-light-success">{lang.labels.open}</span>
      default: return <span className="badge badge-light-secondary">{status}</span>
    }
  }

  return (
    <>
      { fiscalYears === null
        ? <AppPreloader />
        : <div className="card">
            <div className="card-header border-0 pt-6">
              <div className="card-title">
                <h3 className="fw-bolder m-0">{lang.labels.fiscalYears}</h3>
              </div>
              <div className="card-toolbar">
                <button className="btn btn-primary btn-sm" onClick={() => modalShow('modal-create-fiscal-year')}>
                  <span className="svg-icon svg-icon-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <rect opacity="0.5" x="11.364" y="20.364" width="16" height="2" rx="1" transform="rotate(-90 11.364 20.364)" fill="currentColor"/>
                      <rect x="4.364" y="11.364" width="16" height="2" rx="1" fill="currentColor"/>
                    </svg>
                  </span>
                  {lang.labels.newFiscalYear}
                </button>
              </div>
            </div>
            <div className="card-body pt-0">
              { fiscalYears.length === 0
                ? <div className="text-center text-muted py-10">
                    <p className="fs-5">{lang.labels.noFiscalYears}</p>
                  </div>
                : <div className="table-responsive">
                    <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
                      <thead>
                        <tr className="fw-bolder text-muted">
                          <th className="min-w-150px">{lang.labels.name}</th>
                          <th className="min-w-120px">{lang.labels.startDate}</th>
                          <th className="min-w-120px">{lang.labels.endDate}</th>
                          <th className="min-w-100px">{lang.labels.status}</th>
                          <th className="min-w-120px text-end">{lang.labels.actions}</th>
                        </tr>
                      </thead>
                      <tbody>
                        { fiscalYears.map((fy, index) => {
                          const fyId = fy.id ?? fy._id
                          const isOpen = fy.status === 'open'
                          return (
                            <tr key={fyId ?? index}>
                              <td className="fw-bold">{fy.name}</td>
                              <td>{fy.start_date ? dateFormat(lang.code, fy.start_date) : '-'}</td>
                              <td>{fy.end_date ? dateFormat(lang.code, fy.end_date) : '-'}</td>
                              <td>{statusBadge(fy.status)}</td>
                              <td className="text-end">
                                { isOpen &&
                                  <>
                                    <Link to={`/main/accounting/fiscal-year/${fyId}/opening-balance`} className="btn btn-sm btn-light-primary me-2 py-1 px-3">
                                      {lang.labels.openingBalance}
                                    </Link>
                                    <button className="btn btn-sm btn-light-warning me-2 py-1 px-3" onClick={() => openEditModal(fy)}>
                                      {lang.labels.edit}
                                    </button>
                                    <button className="btn btn-sm btn-light-danger me-2 py-1 px-3" onClick={() => handleDelete(fy)} disabled={deleting}>
                                      {lang.labels.delete}
                                    </button>
                                    <button className="btn btn-sm btn-light-danger py-1 px-3" onClick={() => handleClose(fy)} disabled={closing}>
                                      {lang.labels.closeFiscalYear}
                                    </button>
                                  </>
                                }
                                { fy.status === 'closed' && (() => {
                                  const { allowed, blockerName } = canReopenFiscalYear(fy)
                                  return (
                                    <button
                                      className="btn btn-sm btn-light-warning py-1 px-3"
                                      onClick={() => handleReopen(fy)}
                                      disabled={!allowed || reopening}
                                      title={!allowed && blockerName ? lang.labels.reopenFiscalYearFirstReopen.replace('{name}', blockerName) : undefined}
                                    >
                                      { reopening
                                        ? <span className="spinner-border spinner-border-sm align-middle"></span>
                                        : lang.labels.reopenFiscalYear
                                      }
                                    </button>
                                  )
                                })()}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
              }
            </div>
          </div>
      }

      {/* Edit Fiscal Year Modal */}
      <div className="modal fade" id="modal-edit-fiscal-year" tabIndex={-1} aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{lang.labels.editFiscalYear}</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div className="modal-body">
              { editError &&
                <div className="alert alert-danger py-2 px-3 mb-5">{editError}</div>
              }
              <div className="mb-5">
                <label className="form-label fw-bold">{lang.labels.name}</label>
                <input type="text" className="form-control" value={editName} onChange={(e) => setEditName(e.target.value)} />
              </div>
              <div className="mb-5">
                <label className="form-label fw-bold">{lang.labels.startDate}</label>
                <input type="date" className="form-control" value={editStartDate} onChange={(e) => setEditStartDate(e.target.value)} />
              </div>
              <div className="mb-5">
                <label className="form-label fw-bold">{lang.labels.endDate}</label>
                <input type="date" className="form-control" value={editEndDate} onChange={(e) => setEditEndDate(e.target.value)} />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-light" data-bs-dismiss="modal">{lang.labels.cancel}</button>
              <button type="button" className="btn btn-primary" onClick={handleUpdate} disabled={updating || !editName.trim() || !editStartDate || !editEndDate || editEndDate <= editStartDate}>
                { updating
                  ? <span className="spinner-border spinner-border-sm align-middle"></span>
                  : lang.labels.saveChanges
                }
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Create Fiscal Year Modal */}
      <div className="modal fade" id="modal-create-fiscal-year" tabIndex={-1} aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{lang.labels.createFiscalYear}</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div className="modal-body">
              <div className="mb-5">
                <label className="form-label fw-bold">{lang.labels.name}</label>
                <input type="text" className="form-control" value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="2025" />
              </div>
              <div className="mb-5">
                <label className="form-label fw-bold">{lang.labels.startDate}</label>
                <input type="date" className="form-control" value={formStartDate} onChange={(e) => setFormStartDate(e.target.value)} />
              </div>
              <div className="mb-5">
                <label className="form-label fw-bold">{lang.labels.endDate}</label>
                <input type="date" className="form-control" value={formEndDate} onChange={(e) => setFormEndDate(e.target.value)} />
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-light" data-bs-dismiss="modal">{lang.labels.cancel}</button>
              <button type="button" className="btn btn-primary" onClick={handleCreate} disabled={creating || !formName.trim() || !formStartDate || !formEndDate}>
                { creating
                  ? <span className="spinner-border spinner-border-sm align-middle"></span>
                  : lang.labels.createFiscalYear
                }
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default FiscalYearListPage;
